import { useState, useEffect } from 'react';
import { Box, Paper, Typography, Chip, Divider, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { getVlanForIP } from '../data/networkData';
import { getAssetChecks, getAssignedStigs } from '../api/stigMgmt';
import { getAssetVulns } from '../api/tenable';

// ── Deterministic mock data from hostname ──────────────────────

export function hashStr(s = '') {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h;
}

export function mockSecurityData(hostname) {
  const h = hashStr(hostname);
  const critical = (h % 3);
  const high     = ((h >>> 3) % 7);
  const medium   = ((h >>> 6) % 12) + 2;
  const low      = ((h >>> 9) % 18) + 3;
  const stigPct  = 60 + ((h >>> 12) % 38);
  const avDaysAgo = (h >>> 15) % 5;
  const patchDaysAgo = (h >>> 18) % 30;

  const vulnScore   = Math.max(0, 100 - critical * 20 - high * 7 - medium * 2);
  const stigScore   = stigPct;
  const avScore     = avDaysAgo === 0 ? 100 : avDaysAgo < 2 ? 85 : 60;
  const patchScore  = patchDaysAgo < 7 ? 100 : patchDaysAgo < 14 ? 80 : 55;
  const riskScore   = Math.round(vulnScore * 0.4 + stigScore * 0.3 + avScore * 0.15 + patchScore * 0.15);

  return {
    critical, high, medium, low, stigPct, avDaysAgo, patchDaysAgo, riskScore,
    vulnScore, stigScore, avScore, patchScore,
    avDef: `${2024 + (h % 2)}.${String((h >>> 4) % 12 + 1).padStart(2,'0')}.${String((h >>> 8) % 28 + 1).padStart(2,'0')}`,
    openPorts: [22, 80, 443, 8080, 3389].slice(0, 2 + (h % 3)),
    c2cStatus: (h % 4) !== 0 ? 'Compliant' : 'Non-Compliant',
    vulnList: [
      { cve: `CVE-${2024 + (h % 2)}-${10000 + (h % 9999)}`,          desc: 'Remote code execution via crafted HTTP request',       severity: 'Critical', status: 'Open'     },
      { cve: `CVE-${2023 + (h % 2)}-${20000 + ((h >>> 2) % 9999)}`,  desc: 'Privilege escalation through kernel driver',            severity: 'High',     status: 'Open'     },
      { cve: `CVE-${2024 + (h % 2)}-${30000 + ((h >>> 4) % 9999)}`,  desc: 'SMB path traversal allows unauthorized file access',    severity: 'High',     status: 'Open'     },
      { cve: `CVE-${2023 + (h % 2)}-${40000 + ((h >>> 6) % 9999)}`,  desc: 'TLS certificate validation bypass',                    severity: 'Medium',   status: 'Open'     },
      { cve: `CVE-${2022 + (h % 2)}-${50000 + ((h >>> 8) % 9999)}`,  desc: 'Information disclosure via unprotected API endpoint',  severity: 'Medium',   status: 'Open'     },
      { cve: `CVE-${2023 + (h % 2)}-${60000 + ((h >>> 10) % 9999)}`, desc: 'Denial of service via malformed packet',               severity: 'Medium',   status: 'Patched'  },
      { cve: `CVE-${2022 + (h % 2)}-${70000 + ((h >>> 12) % 9999)}`, desc: 'Weak default credentials on management interface',     severity: 'Low',      status: 'Open'     },
      { cve: `CVE-${2021 + (h % 2)}-${80000 + ((h >>> 14) % 9999)}`, desc: 'Outdated library with known memory leak',              severity: 'Low',      status: 'Patched'  },
    ].slice(0, Math.min(critical + high + medium + low, 8)),
  };
}

// ── Sub-components ─────────────────────────────────────────────

function SectionCard({ title, children, sx }) {
  return (
    <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, ...sx }}>
      <Box sx={{ padding: '10px 16px', borderBottom: '1px solid #1e3a5f' }}>
        <Typography variant="subtitle2" sx={{ color: '#ffffff', fontWeight: 700, fontSize: 15 }}>{title}</Typography>
      </Box>
      <Box sx={{ padding: '16px 20px' }}>{children}</Box>
    </Paper>
  );
}

function LabelValue({ label, value, mono, color }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1.25 }}>
      <Typography variant="caption" sx={{ color: '#7a8fa6', fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
      <Typography variant="body2" sx={{ color: color || '#c0cfe0', fontSize: 14, fontFamily: mono ? 'monospace' : 'inherit', fontWeight: mono ? 500 : 400 }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

function RiskGauge({ score }) {
  const color = score >= 85 ? '#4caf50' : score >= 70 ? '#ff9800' : '#f44336';
  const label = score >= 85 ? 'LOW RISK' : score >= 70 ? 'MODERATE' : 'HIGH RISK';

  const r = 120;
  const cx = 160; const cy = 155;
  const startAngle = -210;
  const sweep = 240;
  const pct = Math.min(score / 100, 1);

  function arc(angle) {
    const rad = (angle * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  }
  const s = arc(startAngle);
  const e = arc(startAngle + sweep);
  const f = arc(startAngle + sweep * pct);
  const lg = sweep > 180 ? 1 : 0;
  const lf = sweep * pct > 180 ? 1 : 0;

  const trackPath = `M ${s.x} ${s.y} A ${r} ${r} 0 ${lg} 1 ${e.x} ${e.y}`;
  const fillPath  = pct > 0 ? `M ${s.x} ${s.y} A ${r} ${r} 0 ${lf} 1 ${f.x} ${f.y}` : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <svg width="100%" viewBox="0 0 320 210" style={{ maxWidth: 300 }}>
        <path d={trackPath} fill="none" stroke="#1e3a5f" strokeWidth={18} strokeLinecap="round" />
        {fillPath && <path d={fillPath} fill="none" stroke={color} strokeWidth={18} strokeLinecap="round" />}
        <text x={cx} y={cy}      textAnchor="middle" fill="#ffffff" fontSize={62} fontWeight="700" fontFamily="sans-serif">{score}</text>
        <text x={cx} y={cy + 34} textAnchor="middle" fill={color}   fontSize={15} fontWeight="700" fontFamily="sans-serif" letterSpacing={2}>{label}</text>
      </svg>
    </Box>
  );
}


function EventRow({ time, event, severity }) {
  const color = severity === 'critical' ? '#f44336' : severity === 'warning' ? '#ff9800' : '#7a8fa6';
  return (
    <Box sx={{ display: 'flex', gap: 2, padding: '7px 0', borderBottom: '1px solid #0d1825' }}>
      <Typography variant="caption" sx={{ color: '#4a5a70', fontFamily: 'monospace', fontSize: 13, whiteSpace: 'nowrap', flexShrink: 0 }}>{time}</Typography>
      <Typography variant="caption" sx={{ color, fontSize: 13 }}>{event}</Typography>
    </Box>
  );
}

// ── Device Detail Page ─────────────────────────────────────────

export default function DeviceDetail({ systemId, device, context, onBack, onBackToGroup }) {
  const vlan = device.ip ? getVlanForIP(device.ip) : null;
  const mock = mockSecurityData(device.hostname || '');
  const isOffline = device.status === 'offline' || device.offline;

  const [stigChecks, setStigChecks]       = useState([]);
  const [assignedStigs, setAssignedStigs] = useState([]);
  const [stigLoading, setStigLoading]     = useState(false);
  const [assetVulns, setAssetVulns]       = useState(null);  // null = not yet fetched

  useEffect(() => {
    if (!device.id) return;
    setStigLoading(true);
    Promise.all([getAssetChecks(device.id, systemId), getAssignedStigs(device.id, systemId), getAssetVulns(systemId, device.id)])
      .then(([checks, stigs, vulns]) => {
        setStigChecks(checks);
        setAssignedStigs(stigs);
        setAssetVulns(vulns);
      })
      .catch(() => {})
      .finally(() => setStigLoading(false));
  }, [device.id]);

  const stigStats = (() => {
    if (!stigChecks.length) return null;
    const total         = stigChecks.length;
    const open          = stigChecks.filter(c => c.status === 'fail').length;
    const notAFinding   = stigChecks.filter(c => c.status === 'pass').length;
    const notApplicable = stigChecks.filter(c => c.status === 'not_applicable').length;
    const notReviewed   = stigChecks.filter(c => c.status === 'not_reviewed').length;
    const catI          = stigChecks.filter(c => c.status === 'fail' && c.severity === 'high').length;
    const catII         = stigChecks.filter(c => c.status === 'fail' && c.severity === 'medium').length;
    const catIII        = stigChecks.filter(c => c.status === 'fail' && c.severity === 'low').length;
    const pct           = Math.round((notAFinding / total) * 100);
    return { total, open, notAFinding, notApplicable, notReviewed, catI, catII, catIII, pct };
  })();

  // Normalize IPs to array
  const ips = device.ips || (device.ip ? [device.ip] : []);

  // Mock recent events seeded from hostname
  const h = hashStr(device.hostname || '');
  const mockEvents = [
    { time: '09:14:32', event: 'Successful login from 10.1.0.131 (JUMP-01)', severity: 'info' },
    { time: '08:55:01', event: `AV definition updated to ${mock.avDef}`, severity: 'info' },
    ...(mock.critical > 0 ? [{ time: '06:30:12', event: `Critical vulnerability detected — CVE-${2024 + (h % 2)}-${10000 + (h % 55000)}`, severity: 'critical' }] : []),
    { time: 'Yesterday', event: 'STIG scan completed — ' + mock.stigPct + '% compliant', severity: mock.stigPct < 70 ? 'warning' : 'info' },
    { time: 'Yesterday', event: 'Device checked in to ISE — ' + mock.c2cStatus, severity: mock.c2cStatus !== 'Compliant' ? 'warning' : 'info' },
    { time: '2 days ago', event: 'Configuration backup completed', severity: 'info' },
  ];

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', padding: 3 }}>

      {/* Breadcrumb */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, marginBottom: 2.5 }}>
        <Box onClick={onBack} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', color: '#7a8fa6', '&:hover': { color: '#00bcd4' } }}>
          <ArrowBackIcon sx={{ fontSize: 14 }} />
          <Typography sx={{ fontSize: 13 }}>Assets</Typography>
        </Box>
        <Typography sx={{ color: '#2a3a50', fontSize: 13 }}>/</Typography>
        {context?.fromGroup ? (
          <>
            <Box onClick={onBackToGroup} sx={{ cursor: 'pointer', color: '#7a8fa6', '&:hover': { color: '#00bcd4' } }}>
              <Typography sx={{ fontSize: 13 }}>{context.fromGroup.label}</Typography>
            </Box>
            <Typography sx={{ color: '#2a3a50', fontSize: 13 }}>/</Typography>
            <Typography sx={{ fontSize: 13, color: '#c0cfe0' }}>{device.hostname}</Typography>
          </>
        ) : (
          <Typography sx={{ fontSize: 13, color: '#c0cfe0' }}>{device.hostname}</Typography>
        )}
      </Box>

      {/* Page Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 3 }}>
        <FiberManualRecordIcon sx={{ fontSize: 12, color: isOffline ? '#f44336' : '#4caf50' }} />
        <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 700, fontFamily: 'monospace' }}>
          {device.hostname || 'Unassigned'}
        </Typography>
        <Chip
          label={device.type || 'Physical'}
          size="small"
          sx={{
            backgroundColor: device.type === 'Virtual' ? '#2a1f4a' : '#1e3a5f',
            color: device.type === 'Virtual' ? '#a855f7' : '#00bcd4',
            fontWeight: 700, fontSize: 11,
          }}
        />
        <Chip
          label={isOffline ? 'Offline' : 'Online'}
          size="small"
          sx={{
            backgroundColor: isOffline ? '#2a0a0a' : '#0a2a1a',
            color: isOffline ? '#f44336' : '#4caf50',
            fontWeight: 700, fontSize: 11,
          }}
        />
        {vlan && (
          <Chip
            label={`${vlan.id} · ${vlan.name}`}
            size="small"
            sx={{ backgroundColor: `${vlan.color}22`, color: vlan.color, fontWeight: 700, fontSize: 11 }}
          />
        )}
      </Box>

      {/* Row 1 — Device Identity | Risk Posture Score */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, marginBottom: 2 }}>

        <SectionCard title="Device Identity">
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px' }}>
            <Box>
              <LabelValue label="Hostname"    value={device.hostname} mono />
              {ips.map((ip, i) => (
                <LabelValue key={ip} label={i === 0 ? 'IP Address' : `IP Address ${i + 1}`} value={ip} mono color="#00bcd4" />
              ))}
              <LabelValue label="OS"          value={device.os} />
              <LabelValue label="Purpose"     value={device.purpose} />
              <LabelValue label="Device Type" value={device.type} />
              <LabelValue label="Last Seen"   value={device.lastSeen || (isOffline ? '3+ hrs ago' : '< 1 min ago')} />
            </Box>
            <Box>
              {vlan
                ? <LabelValue label="VLAN"   value={`${vlan.id} — ${vlan.name}`} />
                : <LabelValue label="VLAN"   value="Not assigned" />
              }
              <LabelValue label="Subnet"     value={vlan?.subnet} mono />
              <LabelValue label="Gateway"    value={ips[0]?.split('.').slice(0, 3).join('.') + '.1'} mono />
              <LabelValue label="Open Ports" value={mock.openPorts.join(', ')} mono />
              <LabelValue label="C2C / ISE"  value={mock.c2cStatus} color={mock.c2cStatus === 'Compliant' ? '#4caf50' : '#f44336'} />
            </Box>
          </Box>
          {device.notes && (
            <>
              <Divider sx={{ borderColor: '#1e3a5f', marginY: 1.5 }} />
              <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', display: 'block', marginBottom: 0.5 }}>Notes</Typography>
              <Typography sx={{ color: '#c0cfe0', fontSize: 13 }}>{device.notes}</Typography>
            </>
          )}
        </SectionCard>

        <SectionCard title="Risk Posture Score">
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, alignItems: 'center', height: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {[
                { label: 'Vulnerability Score', score: mock.vulnScore,  weight: 40 },
                { label: 'STIG Compliance',     score: stigStats ? stigStats.pct : mock.stigScore,  weight: 30 },
                { label: 'Antivirus',           score: mock.avScore,    weight: 15 },
                { label: 'Patch Currency',      score: mock.patchScore, weight: 15 },
              ].map(({ label, score, weight }) => {
                const color = score >= 85 ? '#4caf50' : score >= 70 ? '#ff9800' : '#f44336';
                return (
                  <Box key={label}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography sx={{ color: '#c0cfe0', fontSize: 14 }}>{label}</Typography>
                        <Typography sx={{ color: '#4a5a70', fontSize: 11 }}>{weight}%</Typography>
                      </Box>
                      <Typography sx={{ color, fontWeight: 700, fontSize: 18 }}>{score}</Typography>
                    </Box>
                    <Box sx={{ height: 6, borderRadius: 2, backgroundColor: '#1e3a5f' }}>
                      <Box sx={{ height: 6, borderRadius: 2, backgroundColor: color, width: `${score}%`, transition: 'width 0.3s' }} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              <RiskGauge score={mock.riskScore} />
            </Box>
          </Box>
        </SectionCard>

      </Box>

      {/* Row 2 — Antivirus | STIG Compliance */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, marginBottom: 2 }}>

        <SectionCard title="Antivirus">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, marginBottom: 2 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: mock.avDaysAgo < 2 ? '#4caf50' : '#f44336', flexShrink: 0 }} />
            <Typography sx={{ color: mock.avDaysAgo < 2 ? '#4caf50' : '#f44336', fontSize: 28, fontWeight: 700 }}>
              {mock.avDaysAgo < 2 ? 'Current' : 'Outdated'}
            </Typography>
          </Box>
          <LabelValue label="Definition"    value={mock.avDef} mono />
          <LabelValue label="Last Check-in" value={mock.avDaysAgo === 0 ? 'Today' : `${mock.avDaysAgo} day${mock.avDaysAgo > 1 ? 's' : ''} ago`} color={mock.avDaysAgo < 2 ? '#c0cfe0' : '#ff9800'} />
          <LabelValue label="Last Scan"     value={`${mock.patchDaysAgo} days ago`} color={mock.patchDaysAgo > 14 ? '#ff9800' : '#c0cfe0'} />
        </SectionCard>

        <SectionCard title="STIG Compliance">
          {stigLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#00bcd4' }} size={28} /></Box>
          ) : !stigStats ? (
            <Typography sx={{ color: '#4a5a70', fontSize: 13, py: 2 }}>No STIG checks found — apply a STIG from the STIG Compliance page.</Typography>
          ) : (() => {
            const color = stigStats.pct >= 85 ? '#4caf50' : stigStats.pct >= 70 ? '#ff9800' : '#f44336';
            return (
              <>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, marginBottom: 1.5 }}>
                  <Typography sx={{ color, fontWeight: 700, fontSize: 72, lineHeight: 1 }}>{stigStats.pct}</Typography>
                  <Typography sx={{ color, fontSize: 26, fontWeight: 700, paddingBottom: '8px' }}>%</Typography>
                  <Typography sx={{ color: '#7a8fa6', fontSize: 12, paddingBottom: '12px', marginLeft: 0.5 }}>{stigStats.total} checks</Typography>
                </Box>
                <Box sx={{ height: 8, borderRadius: 3, backgroundColor: '#1e3a5f', marginBottom: 2 }}>
                  <Box sx={{ height: 8, borderRadius: 3, width: `${stigStats.pct}%`, backgroundColor: color, transition: 'width 0.3s' }} />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
                  <LabelValue label="Open"            value={stigStats.open}          color={stigStats.open > 0 ? '#f44336' : '#4caf50'} />
                  <LabelValue label="Not a Finding"   value={stigStats.notAFinding}   color="#4caf50" />
                  <LabelValue label="Not Applicable"  value={stigStats.notApplicable} color="#00bcd4" />
                  <LabelValue label="Not Reviewed"    value={stigStats.notReviewed}   color="#7a8fa6" />
                  <LabelValue label="Open CAT I"      value={stigStats.catI}          color={stigStats.catI > 0 ? '#f44336' : '#4a5a70'} />
                  <LabelValue label="Open CAT II"     value={stigStats.catII}         color={stigStats.catII > 0 ? '#ff9800' : '#4a5a70'} />
                </Box>

                {/* Applied STIGs */}
                {assignedStigs.length > 0 && (
                  <>
                    <Divider sx={{ borderColor: '#1e3a5f', my: 1.5 }} />
                    <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
                      Applied STIGs · {assignedStigs.length}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {assignedStigs.map((s, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, padding: '6px 10px', backgroundColor: '#0a0e1a', borderRadius: 1, border: '1px solid #1e3a5f' }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ color: '#c0cfe0', fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {s.stig_title || s.filename}
                            </Typography>
                            {s.version && (
                              <Typography sx={{ color: '#4a5a70', fontSize: 10, fontFamily: 'monospace' }}>
                                V{s.version}R{s.release}
                              </Typography>
                            )}
                          </Box>
                          <Typography sx={{ color: '#4a5a70', fontSize: 10, flexShrink: 0 }}>
                            {s.applied_at ? new Date(s.applied_at).toLocaleDateString() : ''}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </>
            );
          })()}
        </SectionCard>

      </Box>

      {/* Row 3 — Vulnerabilities (full width) */}
      <SectionCard title="Vulnerabilities" sx={{ marginBottom: 2 }}>
        {(() => {
          const SEV_COLOR = { critical: '#f44336', high: '#ff9800', medium: '#ffeb3b', low: '#4caf50' };
          const SEV_LABEL = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' };
          // Use real data if fetched, fall back to mock
          const useReal = assetVulns !== null;
          const counts = useReal
            ? { critical: assetVulns.filter(v => v.severity === 'critical').length,
                high:     assetVulns.filter(v => v.severity === 'high').length,
                medium:   assetVulns.filter(v => v.severity === 'medium').length,
                low:      assetVulns.filter(v => v.severity === 'low').length }
            : { critical: mock.critical, high: mock.high, medium: mock.medium, low: mock.low };
          const total = Object.values(counts).reduce((s, n) => s + n, 0);

          return (
            <Box sx={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 3 }}>
              <Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, marginBottom: 2 }}>
                  {['critical', 'high', 'medium', 'low'].map(sev => {
                    const count = counts[sev];
                    const color = SEV_COLOR[sev];
                    return (
                      <Box key={sev} sx={{ padding: '14px 8px', backgroundColor: '#0a0e1a', borderRadius: 1, textAlign: 'center', border: `1px solid ${count > 0 ? color + '33' : '#1e3a5f'}` }}>
                        <Typography sx={{ color: count > 0 ? color : '#2a3a50', fontSize: 52, fontWeight: 700, lineHeight: 1 }}>{count}</Typography>
                        <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 0.75 }}>{SEV_LABEL[sev]}</Typography>
                      </Box>
                    );
                  })}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid #1e3a5f' }}>
                  <Typography sx={{ color: '#7a8fa6', fontSize: 14 }}>Total findings</Typography>
                  <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: 20 }}>{total}</Typography>
                </Box>
                {!useReal && <Typography sx={{ color: '#4a5a70', fontSize: 10, mt: 1 }}>· Simulated — connect Tenable for real data</Typography>}
              </Box>
              <Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '130px 1fr 90px 80px', padding: '6px 10px', borderBottom: '1px solid #1e3a5f', marginBottom: 0.5 }}>
                  {['Plugin / CVE', 'Title', 'Severity', 'CVSS'].map(h => (
                    <Typography key={h} sx={{ color: '#7a8fa6', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</Typography>
                  ))}
                </Box>
                {useReal ? (
                  assetVulns.length === 0 ? (
                    <Typography sx={{ color: '#4caf50', fontSize: 14, padding: '16px 10px' }}>No open findings — device is clean.</Typography>
                  ) : assetVulns.map((v) => (
                    <Box key={v.id} sx={{ display: 'grid', gridTemplateColumns: '130px 1fr 90px 80px', padding: '10px', borderBottom: '1px solid #0d1825', '&:hover': { backgroundColor: '#0d1f33' } }}>
                      <Box>
                        <Typography sx={{ color: '#00bcd4', fontFamily: 'monospace', fontSize: 11 }}>{v.plugin_id}</Typography>
                        {v.cve_id && <Typography sx={{ color: '#4a5a70', fontFamily: 'monospace', fontSize: 10 }}>{v.cve_id}</Typography>}
                      </Box>
                      <Typography sx={{ color: '#c0cfe0', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.title}</Typography>
                      <Chip label={SEV_LABEL[v.severity] || v.severity} size="small" sx={{
                        backgroundColor: `${SEV_COLOR[v.severity] || '#607d8b'}22`,
                        color: SEV_COLOR[v.severity] || '#607d8b',
                        fontWeight: 700, fontSize: 11, height: 20,
                      }} />
                      <Typography sx={{ color: v.cvss_score >= 9 ? '#f44336' : v.cvss_score >= 7 ? '#ff9800' : '#c0cfe0', fontWeight: 700, fontSize: 13 }}>
                        {v.cvss_score ? v.cvss_score.toFixed(1) : '—'}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  mock.vulnList.map((v, i) => (
                    <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '130px 1fr 90px 80px', padding: '10px', borderBottom: '1px solid #0d1825', '&:hover': { backgroundColor: '#0d1f33' } }}>
                      <Typography sx={{ color: '#00bcd4', fontFamily: 'monospace', fontSize: 12 }}>{v.cve}</Typography>
                      <Typography sx={{ color: '#c0cfe0', fontSize: 13 }}>{v.desc}</Typography>
                      <Chip label={v.severity} size="small" sx={{
                        backgroundColor: SEV_COLOR[v.severity.toLowerCase()] + '22',
                        color: SEV_COLOR[v.severity.toLowerCase()],
                        fontWeight: 700, fontSize: 11, height: 20,
                      }} />
                      <Typography sx={{ color: '#4a5a70', fontSize: 12 }}>{v.status}</Typography>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          );
        })()}
      </SectionCard>

      {/* Row 4 — Recent Events (full width) */}
      <SectionCard title="Recent Events">
        {mockEvents.map((ev, i) => (
          <EventRow key={i} {...ev} />
        ))}
      </SectionCard>
    </Box>
  );
}
